import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { FileText, Check, X, Download, Pen, Type, RotateCcw, AlertCircle, CheckCircle2, Copy, ArrowLeft, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PDFViewer from '../components/ui/PDFViewer';
import FieldSigningViewer from '../components/ui/FieldSigningViewer';

const SignDocument = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sigCanvas = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [sigRequest, setSigRequest] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [signatureType, setSignatureType] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [signing, setSigning] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  
  // Field-based signing state
  const [filledFields, setFilledFields] = useState({});
  const [capturedSignature, setCapturedSignature] = useState(null);
  const [capturedInitial, setCapturedInitial] = useState(null);
  const [showApplyAllDialog, setShowApplyAllDialog] = useState(false);
  const [applyAllType, setApplyAllType] = useState(null);
  const applyAllSigRef = useRef(null);
  const [applyAllTyped, setApplyAllTyped] = useState('');
  const [applyAllMethod, setApplyAllMethod] = useState('draw');

  useEffect(() => {
    fetchSignatureRequest();
  }, [requestId]);

  const fetchSignatureRequest = async () => {
    try {
      const response = await api.get(`/signature-requests/${requestId}`);
      setSigRequest(response.data);
      
      // Set up document preview
      if (response.data.file_id) {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        setPreviewUrl(`${backendUrl}/api/files/${response.data.file_id}/preview?token=${token}`);
      }
      
      // Pre-fill typed name
      if (response.data.signer_name) {
        setTypedName(response.data.signer_name);
      } else if (user) {
        setTypedName(`${user.first_name || ''} ${user.last_name || ''}`.trim());
      }
      
      // Pre-fill already completed fields
      if (response.data.signature_fields) {
        const filled = {};
        response.data.signature_fields.forEach(field => {
          if (field.filled) {
            filled[field.id] = {
              value: field.value,
              signature_data: field.signature_data
            };
          }
        });
        setFilledFields(filled);
      }
    } catch (error) {
      console.error('Failed to fetch signature request:', error);
      toast.error('Failed to load signature request');
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };
  
  const hasSignatureFields = sigRequest?.signature_fields?.length > 0;
  const signatureFields = sigRequest?.signature_fields || [];
  const requiredFieldsCount = signatureFields.filter(f => f.required).length;
  const filledFieldsCount = Object.keys(filledFields).length;
  const allFieldsFilled = signatureFields.length > 0 && filledFieldsCount >= signatureFields.length;
  
  // Count fields by type
  const signatureFieldsOfType = (type) => signatureFields.filter(f => f.field_type === type);
  const unfilledFieldsOfType = (type) => signatureFields.filter(f => f.field_type === type && !filledFields[f.id]);

  const handleFieldFill = (fieldId, data) => {
    setFilledFields(prev => ({
      ...prev,
      [fieldId]: data
    }));
  };
  
  const handleApplyToAll = (type) => {
    setApplyAllType(type);
    setApplyAllTyped('');
    setApplyAllMethod('draw');
    setShowApplyAllDialog(true);
  };
  
  const handleApplyAllSubmit = () => {
    let signatureData = null;
    
    if (applyAllMethod === 'draw') {
      if (!applyAllSigRef.current || applyAllSigRef.current.isEmpty()) {
        toast.error(`Please ${applyAllType === 'signature' ? 'draw your signature' : 'write your initials'}`);
        return;
      }
      signatureData = applyAllSigRef.current.toDataURL('image/png');
    } else {
      if (!applyAllTyped.trim()) {
        toast.error('Please type your name');
        return;
      }
      // Create image from typed text
      const canvas = document.createElement('canvas');
      canvas.width = 300;
      canvas.height = 80;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = applyAllType === 'initial' ? '28px "Brush Script MT", cursive' : 'italic 32px "Brush Script MT", cursive';
      ctx.fillStyle = '#1f2937';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(applyAllTyped, canvas.width / 2, canvas.height / 2);
      signatureData = canvas.toDataURL('image/png');
    }
    
    // Store for future fields
    if (applyAllType === 'signature') {
      setCapturedSignature(signatureData);
    } else if (applyAllType === 'initial') {
      setCapturedInitial(signatureData);
    }
    
    // Apply to all unfilled fields of this type
    const fieldsToFill = unfilledFieldsOfType(applyAllType);
    const newFilledFields = { ...filledFields };
    fieldsToFill.forEach(field => {
      newFilledFields[field.id] = { signature_data: signatureData };
    });
    setFilledFields(newFilledFields);
    
    setShowApplyAllDialog(false);
    toast.success(`Applied to ${fieldsToFill.length} ${applyAllType} field${fieldsToFill.length > 1 ? 's' : ''}`);
  };

  const handleSign = async () => {
    // For field-based signing
    if (hasSignatureFields) {
      if (!allFieldsFilled) {
        toast.error('Please complete all required fields');
        return;
      }
      
      setSigning(true);
      try {
        // Convert filledFields to array format for API
        const filledFieldsArray = Object.entries(filledFields).map(([id, data]) => ({
          id,
          ...data
        }));
        
        const formData = new FormData();
        formData.append('filled_fields', JSON.stringify(filledFieldsArray));
        formData.append('typed_name', typedName);

        await api.post(`/signature-requests/${requestId}/sign`, formData);
        toast.success('Document signed successfully!');
        fetchSignatureRequest();
      } catch (error) {
        console.error('Failed to sign document:', error);
        toast.error(error.response?.data?.detail || 'Failed to sign document');
      } finally {
        setSigning(false);
      }
      return;
    }
    
    // Legacy single-signature flow
    let signatureData = '';
    
    if (signatureType === 'draw') {
      if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
        toast.error('Please draw your signature');
        return;
      }
      signatureData = sigCanvas.current.toDataURL('image/png');
    } else if (signatureType === 'type') {
      if (!typedName.trim()) {
        toast.error('Please type your name');
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 36px "Brush Script MT", cursive';
      ctx.fillStyle = '#1f2937';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
      signatureData = canvas.toDataURL('image/png');
    }

    setSigning(true);
    try {
      const formData = new FormData();
      formData.append('signature_data', signatureData);
      formData.append('signature_type', signatureType);
      if (signatureType === 'type') {
        formData.append('typed_name', typedName);
      }

      await api.post(`/signature-requests/${requestId}/sign`, formData);
      toast.success('Document signed successfully!');
      fetchSignatureRequest();
    } catch (error) {
      console.error('Failed to sign document:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      const formData = new FormData();
      formData.append('reason', declineReason);

      await api.post(`/signature-requests/${requestId}/decline`, formData);
      toast.success('Signature request declined');
      setShowDeclineDialog(false);
      fetchSignatureRequest();
    } catch (error) {
      console.error('Failed to decline:', error);
      toast.error('Failed to decline signature request');
    } finally {
      setDeclining(false);
    }
  };

  const handleDownload = async () => {
    try {
      // If document is signed, download the signed version
      if (sigRequest.status === 'signed') {
        const response = await api.get(`/signature-requests/${requestId}/download-signed`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `signed_${sigRequest.filename || 'document.pdf'}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Download original for unsigned documents
        const response = await api.get(`/files/${sigRequest.file_id}/download`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', sigRequest.filename || 'document.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error('Failed to download document');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!sigRequest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Signature Request Not Found</h2>
          <p className="text-gray-600">This signature request may have expired or been cancelled.</p>
        </div>
      </div>
    );
  }

  const isExpired = sigRequest.status === 'expired';
  const isSigned = sigRequest.status === 'signed';
  const isDeclined = sigRequest.status === 'declined';
  const canSign = sigRequest.status === 'pending' || sigRequest.status === 'viewed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back to Dashboard Button */}
        <div className="mb-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/client/dashboard')}
            className="flex items-center gap-2 hover:bg-white"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>

        {/* Header */}
        <div className="glass rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{sigRequest.title}</h1>
              <p className="text-gray-600 mt-1">{sigRequest.description}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span>Type: {sigRequest.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                {sigRequest.expires_at && (
                  <span>Expires: {new Date(sigRequest.expires_at).toLocaleDateString()}</span>
                )}
                {hasSignatureFields && (
                  <span className="text-teal-600 font-medium">
                    {signatureFields.length} field{signatureFields.length > 1 ? 's' : ''} to complete
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isSigned && (
                <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Signed
                </span>
              )}
              {isDeclined && (
                <span className="px-4 py-2 bg-red-100 text-red-700 rounded-full font-medium flex items-center gap-2">
                  <X className="w-4 h-4" />
                  Declined
                </span>
              )}
              {isExpired && (
                <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-medium">
                  Expired
                </span>
              )}
              <Button variant="outline" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {hasSignatureFields && canSign ? (
          // Field-based signing UI
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Document with fields */}
            <div className="lg:col-span-3 glass rounded-xl overflow-hidden" style={{ height: '75vh' }}>
              <FieldSigningViewer
                pdfUrl={previewUrl}
                fields={signatureFields}
                filledFields={filledFields}
                onFieldFill={handleFieldFill}
                currentSignature={capturedSignature}
                currentInitial={capturedInitial}
                userName={typedName}
              />
            </div>

            {/* Signing panel */}
            <div className="glass rounded-xl p-6 h-fit sticky top-4">
              <h3 className="font-semibold text-gray-900 mb-4">Complete & Sign</h3>
              
              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-teal-600">{filledFieldsCount} / {signatureFields.length}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2 rounded-full transition-all"
                    style={{ width: `${(filledFieldsCount / signatureFields.length) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Quick actions for bulk signing */}
              {unfilledFieldsOfType('signature').length > 1 && (
                <Button
                  variant="outline"
                  className="w-full mb-2"
                  onClick={() => handleApplyToAll('signature')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Apply Signature to All ({unfilledFieldsOfType('signature').length})
                </Button>
              )}
              
              {unfilledFieldsOfType('initial').length > 1 && (
                <Button
                  variant="outline"
                  className="w-full mb-2"
                  onClick={() => handleApplyToAll('initial')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Apply Initial to All ({unfilledFieldsOfType('initial').length})
                </Button>
              )}
              
              {/* Name input */}
              <div className="mb-4">
                <Label>Your Full Name</Label>
                <Input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1"
                />
              </div>
              
              {/* Sign button */}
              <Button 
                onClick={handleSign}
                disabled={signing || !allFieldsFilled}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white mb-3"
              >
                {signing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {allFieldsFilled ? 'Complete Signing' : `Complete All ${signatureFields.length - filledFieldsCount} Remaining Fields`}
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => setShowDeclineDialog(true)}
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" />
                Decline
              </Button>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                Click on highlighted fields in the document to fill them. Your signature and timestamp will be recorded.
              </p>
            </div>
          </div>
        ) : (
          // Legacy or completed view
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Document Preview */}
            <div className="lg:col-span-2 glass rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Document Preview</h3>
              {sigRequest.mime_type === 'application/pdf' && previewUrl ? (
                <div className="h-[600px]">
                  <PDFViewer 
                    url={previewUrl}
                    filename={sigRequest.filename}
                    onDownload={handleDownload}
                  />
                </div>
              ) : previewUrl ? (
                <div className="h-[600px] overflow-auto bg-gray-100 rounded-lg flex items-center justify-center">
                  <img src={previewUrl} alt={sigRequest.filename} className="max-w-full h-auto" />
                </div>
              ) : (
                <div className="h-[600px] flex items-center justify-center bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Document preview not available</p>
                    <Button variant="outline" className="mt-4" onClick={handleDownload}>
                      <Download className="w-4 h-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Signature Panel */}
            <div className="glass rounded-xl p-6">
              {isSigned ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Document Signed</h3>
                  <p className="text-gray-600 mb-4">
                    Signed by {sigRequest.signer_name} on {new Date(sigRequest.signed_at).toLocaleString()}
                  </p>
                  {sigRequest.signature_data && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-2">Signature:</p>
                      <img 
                        src={sigRequest.signature_data} 
                        alt="Signature" 
                        className="max-w-full h-auto mx-auto border rounded"
                        style={{ maxHeight: '100px' }}
                      />
                    </div>
                  )}
                </div>
              ) : isDeclined ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-10 h-10 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Signature Declined</h3>
                  <p className="text-gray-600 mb-2">
                    Declined on {new Date(sigRequest.declined_at).toLocaleString()}
                  </p>
                  {sigRequest.decline_reason && (
                    <p className="text-gray-500 text-sm">Reason: {sigRequest.decline_reason}</p>
                  )}
                </div>
              ) : isExpired ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-10 h-10 text-gray-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Request Expired</h3>
                  <p className="text-gray-600">
                    This signature request expired on {new Date(sigRequest.expires_at).toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-semibold text-gray-900 mb-4">Sign Document</h3>
                  
                  <Tabs value={signatureType} onValueChange={setSignatureType} className="mb-6">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="draw" className="flex items-center gap-2">
                        <Pen className="w-4 h-4" />
                        Draw
                      </TabsTrigger>
                      <TabsTrigger value="type" className="flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        Type
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="draw" className="mt-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                        <SignatureCanvas
                          ref={sigCanvas}
                          canvasProps={{
                            className: 'signature-canvas w-full',
                            style: { width: '100%', height: '150px' }
                          }}
                          backgroundColor="white"
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={clearSignature}
                        className="mt-2"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                    </TabsContent>
                    
                    <TabsContent value="type" className="mt-4">
                      <div className="space-y-4">
                        <div>
                          <Label>Type your full name</Label>
                          <Input
                            value={typedName}
                            onChange={(e) => setTypedName(e.target.value)}
                            placeholder="Your full name"
                            className="mt-1"
                          />
                        </div>
                        <div className="border rounded-lg p-4 bg-white">
                          <p className="text-sm text-gray-500 mb-2">Preview:</p>
                          <p 
                            className="text-2xl text-center text-gray-800"
                            style={{ fontFamily: '"Brush Script MT", cursive', fontStyle: 'italic' }}
                          >
                            {typedName || 'Your signature'}
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <div className="space-y-3">
                    <Button 
                      onClick={handleSign}
                      disabled={signing}
                      className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                    >
                      {signing ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Sign Document
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={() => setShowDeclineDialog(true)}
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline to Sign
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    By signing, you acknowledge that you have read and agree to the contents of this document. 
                    Your signature, IP address, and timestamp will be recorded.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Signature Request</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for declining (optional)</Label>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Please provide a reason for declining..."
              className="mt-2"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDecline}
              disabled={declining}
            >
              {declining ? 'Declining...' : 'Decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Apply to All Dialog */}
      <Dialog open={showApplyAllDialog} onOpenChange={setShowApplyAllDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Apply {applyAllType === 'signature' ? 'Signature' : 'Initials'} to All Fields
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This will apply your {applyAllType} to all {unfilledFieldsOfType(applyAllType).length} unfilled {applyAllType} fields.
            </p>
            
            <Tabs value={applyAllMethod} onValueChange={setApplyAllMethod}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="draw">Draw</TabsTrigger>
                <TabsTrigger value="type">Type</TabsTrigger>
              </TabsList>
              
              <TabsContent value="draw" className="mt-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                  <SignatureCanvas
                    ref={applyAllSigRef}
                    canvasProps={{
                      className: 'w-full',
                      style: { width: '100%', height: '120px' }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => applyAllSigRef.current?.clear()}
                  className="mt-2"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </TabsContent>
              
              <TabsContent value="type" className="mt-4">
                <Input
                  value={applyAllTyped}
                  onChange={(e) => setApplyAllTyped(e.target.value)}
                  placeholder={applyAllType === 'initial' ? 'Your initials (e.g., JD)' : 'Your full name'}
                />
                <div className="border rounded-lg p-4 bg-gray-50 mt-3">
                  <p className="text-sm text-gray-500 mb-2">Preview:</p>
                  <p 
                    className="text-2xl text-center text-gray-800"
                    style={{ fontFamily: '"Brush Script MT", cursive', fontStyle: 'italic' }}
                  >
                    {applyAllTyped || (applyAllType === 'initial' ? 'JD' : 'Your Name')}
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyAllDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyAllSubmit}>
              <Copy className="w-4 h-4 mr-2" />
              Apply to All ({unfilledFieldsOfType(applyAllType).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignDocument;
